/**
 * Local primitives for the assistant module.
 *
 * Self-contained on purpose: the hub and Settings own their own ui files, and reaching
 * across that line would couple this feature to files it doesn't own. Everything here is
 * token-only (src/styles.css), >=44px touch targets, real focus rings, and never leans on
 * colour alone to carry meaning.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

type Variant = 'primary' | 'secondary' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunk',
  ghost: 'text-ink-soft hover:bg-surface-sunk hover:text-ink',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        FOCUS,
        className
      )}
    />
  );
}

/** A pill that drops an example into the input. Full 44px target for a kitchen phone. */
export function ExampleChip({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center rounded-full border border-border bg-surface px-3 text-left text-xs text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink',
        FOCUS
      )}
    >
      {children}
    </button>
  );
}

export const textArea = cx(
  'block w-full resize-y rounded-2xl border border-border bg-surface px-4 py-3 text-base text-ink placeholder:text-ink-faint',
  FOCUS
);

export const textInput = cx(
  'block min-h-11 w-full rounded-2xl border border-border bg-surface px-4 text-base text-ink placeholder:text-ink-faint',
  FOCUS
);

/** Status carries a word and a mark — never colour on its own. */
export function Note({
  tone,
  children,
  role,
}: {
  tone: 'warn' | 'info' | 'good';
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  const tones = {
    warn: 'border-clay/30 bg-clay-soft text-clay',
    info: 'border-border bg-surface-sunk text-ink-soft',
    good: 'border-sage/30 bg-sage-soft text-sage',
  } as const;
  const mark = { warn: '⚠', info: 'ⓘ', good: '✓' } as const;

  return (
    <p
      role={role}
      className={cx(
        'flex items-start gap-2 rounded-xl border px-3 py-2 text-sm leading-relaxed',
        tones[tone]
      )}
    >
      <span aria-hidden="true">{mark[tone]}</span>
      <span>{children}</span>
    </p>
  );
}

/** A calm "working on it" indicator with a live-region label for screen readers. */
export function Thinking({ label = 'Thinking…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-ink-soft">
      <span className="flex gap-1" aria-hidden="true">
        <span className="h-2 w-2 animate-pulse rounded-full bg-honey [animation-delay:0ms]" />
        <span className="h-2 w-2 animate-pulse rounded-full bg-honey [animation-delay:150ms]" />
        <span className="h-2 w-2 animate-pulse rounded-full bg-honey [animation-delay:300ms]" />
      </span>
      {label}
    </div>
  );
}

export function SparkIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3l1.7 4.6L18.5 9l-4.8 1.4L12 15l-1.7-4.6L5.5 9l4.8-1.4Z" />
      <path d="M18.5 13.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8Z" />
    </svg>
  );
}
